import { useRef } from 'react';
import { TableRow, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    IModuleWeight,
    ITrainingMatrixModuleFragment,
    IPortfolioItemMatrixListItemFragment,
} from 'graphql/types';
import { TableCell } from 'common/components/Table';
import { getModuleTitle, getModuleWeightValue } from 'training/utils/module';
import {
    TrainingParticipantReportMatrixCell,
    TMatrixCellRef,
} from 'training/components/TrainingParticipantMatrix';

interface IProps {
    disabled?: boolean;
    module: ITrainingMatrixModuleFragment;
    report?: IPortfolioItemMatrixListItemFragment;
    trainingId: string;
    userId: string;
    onClearCache?: () => void;
}

export const TrainingParticipantReportTableRow = ({
    disabled,
    module,
    report,
    trainingId,
    userId,
    onClearCache,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const reportCellRef = useRef<TMatrixCellRef>(null);

    const { __typename: moduleTypeName } = module;
    const canClickRow =
        (!!report || moduleTypeName === 'AssignmentModule') && !disabled;

    const handleRowClick = () => {
        if (!canClickRow) return;

        reportCellRef.current?.handleClick();
    };

    return (
        <TableRow
            hover={canClickRow}
            sx={[canClickRow ? { cursor: 'pointer' } : { cursor: 'default' }]}
            onClick={handleRowClick}
            {...other}
        >
            <TableCell>{getModuleTitle(module)}</TableCell>
            <TableCell sx={{ width: '1px' }}>
                {/* Show weight value of module if set and not 1 */}
                {!!('weight' in module) &&
                    module.weight !== IModuleWeight.Weight1 && (
                        <Tooltip title={translate('moduleWeightExplanation')}>
                            <span>{getModuleWeightValue(module.weight)}</span>
                        </Tooltip>
                    )}
            </TableCell>
            <TrainingParticipantReportMatrixCell
                align="center"
                canManageParticipant={true}
                disableClick={disabled}
                key={module.id}
                module={module}
                ref={reportCellRef}
                report={report}
                sx={{ width: '1px' }}
                trainingId={trainingId}
                userId={userId}
                onClearCache={onClearCache}
            />
        </TableRow>
    );
};
